// Table primitive with built-in loading / error / empty states and an opt-in
// card layout below `sm`.
//
//   <Table minWidth={720} mobileMode="cards" empty={items.length === 0 ? {...} : undefined}>
//     <TableHeader>
//       <TableHeaderCell>Title</TableHeaderCell>
//       <TableHeaderCell align="right">Actions</TableHeaderCell>
//     </TableHeader>
//     <TableBody>
//       {items.map((item) => (
//         <TableRow key={item.id}>
//           <TableCell>{item.title}</TableCell>                     {/* headline cell */}
//           <TableCell mobileLabel="Status" nowrap>...</TableCell>  {/* labelled in cards mode */}
//           <TableCell align="right" mobileVariant="actions">...</TableCell>
//         </TableRow>
//       ))}
//     </TableBody>
//   </Table>
//
// - `isLoading` → shimmer rows replace the body (header stays)
// - `error`     → one tinted row replaces the body
// - `empty`     → dashed tile replaces the whole table
// - `mobileMode="cards"` → below `sm` each row renders as a card

import { createContext, useContext } from 'react';
import clsx from 'clsx';
import { useAdminUiLabels } from '../i18n';
import type {
  HTMLAttributes,
  ReactNode,
  TableHTMLAttributes,
  ThHTMLAttributes,
  TdHTMLAttributes,
} from 'react';

type Align = 'left' | 'right' | 'center';

const ALIGN: Record<Align, string> = {
  left: 'text-left',
  right: 'text-right',
  center: 'text-center',
};

type MobileMode = 'scroll' | 'cards';

// Lets sub-components (TableHeader / TableBody / TableRow / TableCell) know
// whether the enclosing Table opted into card-mode. Default is 'scroll' so
// stand-alone use of any sub-component (rare) stays predictable.
const TableContext = createContext<{ mobileMode: MobileMode }>({ mobileMode: 'scroll' });

// ───────────────────────────────────────────────────────────────────────
// <Table> — outer wrapper. Owns the overflow + min-width + state chrome.
// ───────────────────────────────────────────────────────────────────────

export interface TableEmptyState {
  /** Heading line (e.g. "No articles yet"). */
  title: string;
  /** Sub-line (optional). */
  description?: string | undefined;
  /** Optional CTA — typically a <Button> to create the first row. */
  action?: ReactNode | undefined;
}

// All optional fields explicitly include `| undefined` because tsconfig has
// `exactOptionalPropertyTypes: true` — without it, callers can't write
// `empty={cond ? {...} : undefined}` (the common pattern when the empty
// state depends on data).
export interface TableProps extends Omit<TableHTMLAttributes<HTMLTableElement>, 'children'> {
  children: ReactNode;
  /**
   * Minimum table width in px. Below this the wrapper scrolls horizontally
   * instead of squeezing cells. Default 720 (= JobsTable's reference width).
   * Ignored at <sm when `mobileMode="cards"` — cards stack vertically with
   * no horizontal scroll.
   */
  minWidth?: number | undefined;
  /**
   * Mobile rendering strategy. `scroll` (default) keeps the table as a table
   * at every viewport, scrolling horizontally when narrower than `minWidth`.
   * `cards` morphs each row into a stacked card at <sm (640px) so mobile
   * users don't have to horizontal-scroll to reach action columns.
   */
  mobileMode?: MobileMode | undefined;
  /** When true, replace the body with shimmer rows. Header stays visible. */
  isLoading?: boolean | undefined;
  /** Number of shimmer rows to render when isLoading. Default 3. */
  loadingRows?: number | undefined;
  /** When set, replace the body with an inline error row. */
  error?: unknown;
  /** When set AND children produced no data rows, replace the entire table
      with a dashed-border empty-state tile. Pass undefined when rows exist. */
  empty?: TableEmptyState | undefined;
}

export function Table({
  children,
  minWidth = 720,
  mobileMode = 'scroll',
  isLoading,
  loadingRows = 3,
  error,
  empty,
  className,
  style,
  ...rest
}: TableProps) {
  const labels = useAdminUiLabels();
  // Empty state pre-empts everything — render the dashed tile and bail.
  // Matches the previous JobsTable / team.tsx / licenses.tsx patterns
  // that all dropped the table entirely on no-data.
  if (empty && !isLoading && !error) {
    return (
      <div
        role="status"
        className="rounded-[var(--radius-lg)] border border-dashed border-[var(--border-strong)] bg-[var(--bg-page)] px-6 py-12 text-center"
      >
        <p className="font-[var(--font-display)] text-base font-semibold text-[var(--fg-default)]">
          {empty.title}
        </p>
        {empty.description ? (
          <p className="mt-1 font-[var(--font-body)] text-sm text-[var(--fg-muted)]">
            {empty.description}
          </p>
        ) : null}
        {empty.action ? <div className="mt-4 inline-flex">{empty.action}</div> : null}
      </div>
    );
  }

  const isCards = mobileMode === 'cards';

  return (
    <TableContext.Provider value={{ mobileMode }}>
      {/* Wrapper: hides its own chrome at <sm in cards mode — each row's
          card draws its own border + bg + shadow, so the wrapper would
          look like a doubled outline otherwise. */}
      <div
        className={clsx(
          'overflow-x-auto rounded-[var(--radius-lg)] border border-[var(--mist-gray)] bg-[var(--bg-page)] shadow-[var(--shadow-xs)]',
          isCards &&
            'max-sm:overflow-visible max-sm:rounded-none max-sm:border-0 max-sm:bg-transparent max-sm:shadow-none',
        )}
      >
        <table
          {...rest}
          // CSS var carries min-width so the max-sm:min-w-0 override can win
          // without `!important` — inline `style` would otherwise out-rank
          // any Tailwind class.
          style={{ minWidth: `${minWidth}px`, ...(style ?? {}) }}
          className={clsx(
            'w-full font-[var(--font-body)] text-sm',
            // `!` modifier marks the override important so it beats the inline
            // `style={{ minWidth }}` above — without it, the table kept its
            // desktop min-width at <sm and the row-as-card layout still
            // forced horizontal scroll (caught by the visual audit, missed
            // by the static review that approved #714).
            isCards && 'max-sm:block max-sm:![min-width:0]',
            className,
          )}
        >
          {/* If loading or error, render a sentinel body in place of children.
              We still render the header (passed in as the first child) so the
              user sees the column structure during data fetch. */}
          {isLoading ? (
            <>
              {extractHeader(children)}
              <tbody className={clsx(isCards && 'max-sm:block')}>
                {Array.from({ length: loadingRows }).map((_, i) => (
                  <tr key={i} className={clsx('border-t border-[var(--mist-gray)]', isCards && 'max-sm:block')}>
                    {/* colSpan=999: HTML clamps to the actual column count,
                        so a single TD spans the full row regardless of how
                        many <TableHeaderCell>s the caller declared. */}
                    <td colSpan={999} className={clsx('px-4 py-3', isCards && 'max-sm:block')}>
                      <div className="h-4 w-full animate-pulse rounded bg-[var(--mist-gray)]" />
                    </td>
                  </tr>
                ))}
              </tbody>
            </>
          ) : error ? (
            <>
              {extractHeader(children)}
              <tbody className={clsx(isCards && 'max-sm:block')}>
                <tr className={clsx(isCards && 'max-sm:block')}>
                  <td
                    colSpan={999}
                    className={clsx(
                      'bg-[rgba(214,69,69,0.06)] px-4 py-6 text-center text-sm text-[var(--color-danger)]',
                      isCards && 'max-sm:block',
                    )}
                    role="alert"
                  >
                    {labels.tableLoadError} {messageFromError(error, labels.tableReload)}
                  </td>
                </tr>
              </tbody>
            </>
          ) : (
            children
          )}
        </table>
      </div>
    </TableContext.Provider>
  );
}

// ───────────────────────────────────────────────────────────────────────
// <TableHeader> / <TableHeaderCell> — <thead> wrapper + <th> cell
// ───────────────────────────────────────────────────────────────────────

export interface TableHeaderProps extends HTMLAttributes<HTMLTableSectionElement> {
  children: ReactNode;
}

export function TableHeader({ children, className, ...rest }: TableHeaderProps) {
  const { mobileMode } = useContext(TableContext);
  return (
    <thead
      {...rest}
      className={clsx(
        'border-b border-[var(--mist-gray)] bg-[var(--cloud-gray)]',
        'font-[var(--font-display)] text-[11px] font-semibold uppercase tracking-[var(--tracking-caps)] text-[var(--slate-700)]',
        // Hidden at <sm in cards mode — each TableCell renders its column
        // label inline via `mobileLabel`, so a separate header would be
        // visual noise on mobile.
        mobileMode === 'cards' && 'max-sm:hidden',
        className,
      )}
    >
      <tr>{children}</tr>
    </thead>
  );
}

export interface TableHeaderCellProps extends ThHTMLAttributes<HTMLTableCellElement> {
  children?: ReactNode;
  align?: Align;
}

export function TableHeaderCell({ children, align = 'left', className, ...rest }: TableHeaderCellProps) {
  return (
    <th {...rest} scope="col" className={clsx('px-4 py-2', ALIGN[align], className)}>
      {children}
    </th>
  );
}

// ───────────────────────────────────────────────────────────────────────
// <TableBody> / <TableRow> / <TableCell>
// ───────────────────────────────────────────────────────────────────────

export interface TableBodyProps extends HTMLAttributes<HTMLTableSectionElement> {
  children: ReactNode;
}

export function TableBody({ children, className, ...rest }: TableBodyProps) {
  const { mobileMode } = useContext(TableContext);
  return (
    <tbody
      {...rest}
      className={clsx(
        'divide-y divide-[var(--mist-gray)]',
        // Cards mode at <sm: flex column of cards with gap; cancel the
        // divide-y (rows are no longer dividers, they're standalone cards).
        mobileMode === 'cards' && 'max-sm:flex max-sm:flex-col max-sm:gap-3 max-sm:divide-y-0',
        className,
      )}
    >
      {children}
    </tbody>
  );
}

export interface TableRowProps extends HTMLAttributes<HTMLTableRowElement> {
  children: ReactNode;
}

export function TableRow({ children, className, ...rest }: TableRowProps) {
  const { mobileMode } = useContext(TableContext);
  return (
    <tr
      {...rest}
      className={clsx(
        'transition-colors hover:bg-[var(--cloud-gray)]',
        // Cards mode at <sm: row becomes a self-contained card. Disable the
        // table-row hover bg (would flash awkwardly on a card surface).
        mobileMode === 'cards' &&
          'max-sm:block max-sm:rounded-[var(--radius-md)] max-sm:border max-sm:border-[var(--mist-gray)] max-sm:bg-[var(--bg-page)] max-sm:p-3 max-sm:shadow-[var(--shadow-xs)] max-sm:hover:bg-[var(--bg-page)]',
        className,
      )}
    >
      {children}
    </tr>
  );
}

export interface TableCellProps extends TdHTMLAttributes<HTMLTableCellElement> {
  children?: ReactNode;
  align?: Align;
  /** Prevent the cell content from wrapping. Useful for currency / dates / action columns. */
  nowrap?: boolean;
  /**
   * In `mobileMode="cards"` at <sm, prepend this label as a small caption
   * line before the cell content (e.g. `mobileLabel="Status"` → renders
   * "Status" above the value). Omit on the headline cell (first
   * column with title + subtitle) and on the actions cell — those want
   * to render as the card's title block and footer respectively.
   * Ignored at ≥sm and in `mobileMode="scroll"`.
   */
  mobileLabel?: string | undefined;
  /**
   * In `mobileMode="cards"` at <sm, marks this cell with a special
   * treatment. `actions` adds a top-border separator above the cell so
   * the row-action button cluster reads as a distinct footer on the card.
   */
  mobileVariant?: 'actions' | undefined;
}

export function TableCell({
  children,
  align = 'left',
  nowrap,
  mobileLabel,
  mobileVariant,
  className,
  ...rest
}: TableCellProps) {
  const { mobileMode } = useContext(TableContext);
  const isCards = mobileMode === 'cards';
  return (
    <td
      {...rest}
      className={clsx(
        'px-4 py-3 text-[var(--fg-default)]',
        ALIGN[align],
        nowrap && 'whitespace-nowrap',
        // Cards mode at <sm: each cell becomes a block, no horizontal padding
        // (the row's p-3 handles outer gutter), tighter vertical rhythm,
        // and left-aligned regardless of `align` (the desktop right-align
        // for action columns reads as misalignment inside a card).
        isCards && 'max-sm:block max-sm:px-0 max-sm:py-1 max-sm:text-left',
        // Actions variant: separator above so the action cluster reads as
        // a distinct footer band on the card.
        isCards &&
          mobileVariant === 'actions' &&
          'max-sm:mt-2 max-sm:border-t max-sm:border-[var(--mist-gray)] max-sm:pt-3',
        className,
      )}
    >
      {/* Inline caption in cards mode. The `max-sm:` hides this at ≥sm so
          desktop cells render exactly as before. */}
      {isCards && mobileLabel ? (
        <span className="hidden max-sm:mr-2 max-sm:inline-block max-sm:text-xs max-sm:font-medium max-sm:uppercase max-sm:tracking-[var(--tracking-caps)] max-sm:text-[var(--fg-muted)]">
          {mobileLabel}:
        </span>
      ) : null}
      {children}
    </td>
  );
}

// ───────────────────────────────────────────────────────────────────────
// <TableRowAction> — icon-only button used inside an action column.
// Shared between JobsTable + team (Invitations + Members) + future tables.
//
// Lives next to the slot components because every table's row-action
// column lands on the same shape (32×32 icon, hover bg, focus ring,
// optional destructive tint). Keeping the helper here means a 4th
// consumer doesn't have to reinvent the styling.
//
// Caller passes `icon` (a lucide element) + `title` (native tooltip,
// shown on hover) + `ariaLabel` (verbose label for screen readers).
// The native title is the lightweight tooltip surface; if richer
// tooltip behavior becomes a need, swap in a React Aria Tooltip here
// and every consumer picks it up.
// ───────────────────────────────────────────────────────────────────────

export interface TableRowActionProps {
  /** Native browser tooltip on hover (e.g. "Edit article"). Brief. */
  title: string;
  /** Verbose accessibility label that names the target (e.g. "Edit ${title}"). */
  ariaLabel: string;
  /** Typically a lucide-react icon, h-4 w-4. */
  icon: ReactNode;
  onClick: () => void;
  disabled?: boolean | undefined;
  /** Red-tinted hover + text for delete / revoke / disable actions. */
  destructive?: boolean | undefined;
}

const ROW_ACTION_BASE =
  // 44px on touch, 32px from `sm` up. WCAG 2.5.5 / iOS HIG put the floor at
  // 44px; 32px is comfortable with a mouse and too small with a thumb, and
  // these rows carry up to 8 actions including DELETE.
  'inline-flex h-11 w-11 sm:h-8 sm:w-8 items-center justify-center rounded-[var(--radius-sm)] ' +
  'text-[var(--fg-muted)] transition-colors ' +
  'hover:bg-[var(--cloud-gray)] hover:text-[var(--color-trust-blue)] ' +
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-trust-blue)] focus-visible:ring-offset-1 ' +
  'disabled:cursor-not-allowed disabled:opacity-50';

const ROW_ACTION_DESTRUCTIVE = 'hover:bg-[rgba(214,69,69,0.08)] hover:!text-[var(--color-danger)]';

export function TableRowAction({
  title,
  ariaLabel,
  icon,
  onClick,
  disabled,
  destructive,
}: TableRowActionProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title}
      aria-label={ariaLabel}
      className={destructive ? `${ROW_ACTION_BASE} ${ROW_ACTION_DESTRUCTIVE}` : ROW_ACTION_BASE}
    >
      {icon}
    </button>
  );
}

// ───────────────────────────────────────────────────────────────────────
// helpers
// ───────────────────────────────────────────────────────────────────────

/** Extract the first <TableHeader> from the children so it can render under
 *  the loading / error sentinels. We deliberately don't recurse — the
 *  primitive expects a flat <TableHeader>+<TableBody> structure. */
function extractHeader(children: ReactNode): ReactNode {
  // React.Children utilities would work but the typical compositional usage
  // is `<>{header}{body}</>` or just `[header, body]` as direct children;
  // both render fine when filtered by `type`. Loose check by structure: the
  // first element in `children` is assumed to be the header per the API.
  if (Array.isArray(children)) {
    return children[0];
  }
  return null;
}

function messageFromError(error: unknown, fallback: string): string {
  if (error instanceof Error) return error.message;
  if (typeof error === 'string') return error;
  return fallback;
}
